/**
 * My harvest detail (web) — the G1 farmer's own harvest (status, total kg,
 * weight rows, add-weight, Lock).
 *
 * Web port of apps/mobile/app/my-harvests/[id].tsx. A harvest is the owner's
 * own — the contributor is implicit (the signed-in user), enforced server-side
 * by the owner-scope check; there's no farmer picker and no farmerUserId on the
 * rows. Back navigation returns to /my-harvests.
 *
 * READ-ONLY in a coop: the add-weight + lock affordances are hidden when the
 * farmer belongs to a cooperative (farm-profile.cooperativeId !== null). We fail
 * OPEN: on a farm-profile read error/404 we treat the user as NOT in a coop.
 *
 * Unlike mobile, web is ONLINE-ONLY — there is no SQLite cache, no sync-engine
 * outbox, and no offline `clientOpId`. A transport failure just surfaces as an
 * error; we also surface `FseApiError.message` if the server rejects a stale
 * action (e.g. the harvest was locked in another tab).
 *
 * Endpoints: GET /harvests/:id, GET /harvests/:id/contributions,
 * POST /harvests/:id/contributions, PATCH /harvests/:id/lock,
 * GET /me/farm-profile. Uses the generated orval clients
 * src/api/generated/harvests + .../me (TD-070).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  ListItem,
  Stack,
  Text,
  TextField,
  type ChipColor,
} from '@fse/ui';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError, readAccessTokenClaims } from '../api/client';
import {
  fetchHarvestPhotoObjectUrl,
  listHarvestPhotos,
  uploadHarvestPhoto,
  type HarvestPhotoDto,
} from '../api/photos';
import { meControllerGetFarmProfile } from '../api/generated/me/me';
import { groupDigits } from '../lib/format';
import {
  harvestsControllerAddContribution,
  harvestsControllerGetOne,
  harvestsControllerListContributions,
  harvestsControllerLock,
  harvestsControllerRecordSale,
} from '../api/generated/harvests/harvests';
import {
  HarvestDtoStatus,
  type HarvestContributionListDto,
  type HarvestDto,
} from '../api/generated/models';

/**
 * Map harvest lifecycle → kit `Chip` colour (assist chip used as a status
 * badge). Mirrors the list screen mapping.
 */
const STATUS_CHIP: Record<HarvestDtoStatus, ChipColor> = {
  open: 'neutral',
  locked: 'warning',
  listed: 'success',
  sold: 'success',
  settled: 'success',
  cancelled: 'neutral',
};

/**
 * Chip colour for a harvest status, defensive against the deferred-regen gap:
 * `'listed'` exists at runtime but isn't a key in STATUS_CHIP yet, so we
 * string-key the lookup → 'success' (leaf) for 'listed', the mapped colour for
 * known statuses, 'neutral' as a fallback for anything unexpected.
 */
function chipColorFor(status: string): ChipColor {
  if (status === 'listed') return 'success';
  return STATUS_CHIP[status as HarvestDtoStatus] ?? 'neutral';
}

/** Client-side upload guard — mirrors the server's ≤5 MB image-only limit. */
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

function shortId(id: string): string {
  return id.slice(0, 8);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Format an ISO-8601 timestamp to a readable local date-time, guarding NaN. */
function formatRecordedAt(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString();
}

// --------------------------------------------------------------------------
// List a LOCKED harvest on the marketplace (Phase 5). This used to be an inline
// publish form; it now routes the farmer into the richer guided flow
// (/create-listing?harvestId=<id>, the "CreateListing" lane) which adds a photo
// step + grade. The gate is unchanged: only a LOCKED harvest is listable (open
// still takes weights; sold/settled is done) and never for coop members — coop
// harvests are coop-managed and the server 403s them regardless. Copy is
// hardcoded English to mirror the batch card (this producer affordance follows
// the batch pattern, TD-088).
// --------------------------------------------------------------------------
function PublishHarvestListingSection({
  harvestId,
  status,
  isInCoop,
  t,
}: {
  harvestId: string;
  // Widened to string: 'listed' is a real runtime status (locked → listed on
  // publish) but isn't in the orval HarvestDtoStatus union yet (regen deferred
  // until after deploy). We compare against the string literal defensively.
  status: HarvestDtoStatus | 'listed';
  isInCoop: boolean;
  t: TFunction;
}) {
  // Once the harvest is LISTED on the marketplace, the publish CTA no longer
  // applies — show a small confirmation note instead (coop members never see
  // either; their harvests are coop-managed and the server 403s a publish).
  if (status === 'listed' && !isInCoop) {
    return (
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Box component="span" sx={{ color: 'success.main', display: 'inline-flex' }}>
              <Icon name="storefront" />
            </Box>
            <Text variant="body-medium" color="text.secondary">
              {t('myHarvests.listedNote')}
            </Text>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (status !== 'locked' || isInCoop) return null;

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Text variant="title-medium" as="span">
            List on marketplace
          </Text>
          <Text variant="body-small" color="text.secondary">
            Publish this harvest so verified buyers can make offers. A guided flow
            walks you through the asking price, an optional grade, and a buyer-visible
            cover photo. You accept or reject each offer.
          </Text>
          <Box>
            {/* Plain anchor href (not a typed <Link to>) so this file stays
                decoupled from the route registration the CreateListing lane's
                central wiring owns; the guided screen reads `?harvestId=` off the
                URL itself. */}
            <Button
              variant="contained"
              href={`/create-listing?harvestId=${encodeURIComponent(harvestId)}`}
              startIcon={<Icon name="storefront" />}
            >
              List on marketplace
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function MyHarvestDetailScreen() {
  const { t } = useTranslation();
  const sub = readAccessTokenClaims()?.sub;

  // Route is wired manually in router.ts with path '/my-harvests/$id'. Read the
  // param loosely (strict: false) so this file doesn't depend on a generated
  // route-id type that doesn't exist in the manually-assembled tree.
  const params = useParams({ strict: false }) as { id?: string };
  const harvestId = params.id ?? '';

  // Read-only when in a coop. Fail OPEN: on error/404 treat as NOT in a coop.
  const farmProfileQuery = useQuery({
    queryKey: ['me-farm-profile'],
    queryFn: ({ signal }) => meControllerGetFarmProfile(signal),
    enabled: !!sub,
    retry: false,
  });
  const isInCoop = farmProfileQuery.data?.cooperativeId != null;

  const harvestQuery = useQuery({
    queryKey: ['my-harvests', harvestId],
    queryFn: ({ signal }) => harvestsControllerGetOne(harvestId, signal),
    enabled: harvestId.length > 0,
  });

  const contributionsQuery = useQuery({
    queryKey: ['harvest-contributions', harvestId],
    queryFn: ({ signal }) => harvestsControllerListContributions(harvestId, undefined, signal),
    enabled: harvestId.length > 0,
  });

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Text variant="headline-medium" as="h2">
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {harvestQuery.data
              ? t('myHarvests.card', { id: shortId(harvestQuery.data.id) })
              : t('myHarvests.title')}
          </Box>
        </Text>
        <Link to="/my-harvests" style={{ textDecoration: 'none' }}>
          <Button variant="text">{t('myHarvests.title')}</Button>
        </Link>
      </Box>

      {isInCoop ? <Alert severity="info">{t('myHarvests.coopReadOnly')}</Alert> : null}

      {harvestQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : harvestQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => harvestQuery.refetch()}>
              {t('myHarvests.retry')}
            </Button>
          }
        >
          {t('myHarvests.detail.notFound')}
        </Alert>
      ) : (
        <HarvestDetailBody
          harvest={harvestQuery.data}
          contributions={contributionsQuery}
          isInCoop={isInCoop}
          t={t}
        />
      )}
    </Stack>
  );
}

interface HarvestDetailBodyProps {
  harvest: HarvestDto;
  contributions: UseQueryResult<HarvestContributionListDto>;
  isInCoop: boolean;
  t: TFunction;
}

function HarvestDetailBody({ harvest, contributions, isInCoop, t }: HarvestDetailBodyProps) {
  const queryClient = useQueryClient();
  const isOpen = harvest.status === 'open';
  const canMutate = isOpen && !isInCoop;
  // Record-sale action: only an independent farmer on a LOCKED harvest (same
  // gate as add/lock — coop members never see it; the server 403s regardless).
  const showRecordSale = harvest.status === 'locked' && !isInCoop;
  // Once sold/settled, show the recorded sale details instead of the action.
  const showSale = harvest.status === 'sold' || harvest.status === 'settled';

  const [addOpen, setAddOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [kg, setKg] = useState('');

  // Record-sale form (locked → sold). Currency is fixed to ZAR for now.
  const [saleOpen, setSaleOpen] = useState(false);
  const [soldTo, setSoldTo] = useState('');
  const [saleAmount, setSaleAmount] = useState('');
  const [saleDate, setSaleDate] = useState(todayIso);
  const [paymentMethod, setPaymentMethod] = useState('');

  const rows = contributions.data?.data ?? [];

  const addMutation = useMutation({
    mutationFn: (qtyKg: number) =>
      // Web is online-only — no clientOpId (mobile-only idempotency key).
      harvestsControllerAddContribution(harvest.id, { qtyKg, harvestDate: todayIso() }),
    onSuccess: () => {
      setKg('');
      setAddOpen(false);
      queryClient.invalidateQueries({ queryKey: ['harvest-contributions', harvest.id] });
      queryClient.invalidateQueries({ queryKey: ['my-harvests', harvest.id] });
      queryClient.invalidateQueries({ queryKey: ['my-harvests'] });
    },
  });

  const lockMutation = useMutation({
    mutationFn: () => harvestsControllerLock(harvest.id),
    onSuccess: () => {
      setLockOpen(false);
      queryClient.invalidateQueries({ queryKey: ['my-harvests', harvest.id] });
      queryClient.invalidateQueries({ queryKey: ['my-harvests'] });
    },
  });

  // Record a PRIVATE off-platform sale (locked → sold). No buyer account, no
  // payments — a personal sales-ledger record. The server enforces status=locked
  // + owner-scope (400/403/404 otherwise). saleAmount goes over the wire as a
  // number per RecordSaleDto; currency is fixed to ZAR for now.
  const saleMutation = useMutation({
    mutationFn: (vars: {
      soldTo: string;
      saleAmount: number;
      saleDate: string;
      paymentMethod?: string;
    }) =>
      harvestsControllerRecordSale(harvest.id, {
        soldTo: vars.soldTo,
        saleAmount: vars.saleAmount,
        saleDate: vars.saleDate,
        saleCurrency: 'ZAR',
        ...(vars.paymentMethod ? { paymentMethod: vars.paymentMethod } : {}),
      }),
    onSuccess: () => {
      setSaleOpen(false);
      setSoldTo('');
      setSaleAmount('');
      setSaleDate(todayIso());
      setPaymentMethod('');
      queryClient.invalidateQueries({ queryKey: ['my-harvests', harvest.id] });
      queryClient.invalidateQueries({ queryKey: ['my-harvests'] });
    },
  });

  const kgNum = Number(kg);
  const kgValid = kg.trim().length > 0 && Number.isFinite(kgNum) && kgNum > 0;

  const saleAmountNum = Number(saleAmount);
  const saleAmountValid =
    saleAmount.trim().length > 0 && Number.isFinite(saleAmountNum) && saleAmountNum > 0;
  const saleValid =
    soldTo.trim().length > 0 && saleAmountValid && saleDate.trim().length > 0;

  function closeAdd() {
    if (addMutation.isPending) return;
    setKg('');
    addMutation.reset();
    setAddOpen(false);
  }

  function closeLock() {
    if (lockMutation.isPending) return;
    lockMutation.reset();
    setLockOpen(false);
  }

  function closeSale() {
    if (saleMutation.isPending) return;
    saleMutation.reset();
    setSaleOpen(false);
  }

  return (
    <Stack spacing={3}>
      {/* Header card — short id, status chip, total kg */}
      <Card>
        <CardContent>
          <Stack spacing={1}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Text variant="title-large" as="span">
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  {t('myHarvests.card', { id: shortId(harvest.id) })}
                </Box>
              </Text>
              <Chip
                kind="assist"
                color={chipColorFor(harvest.status)}
                label={t(`myHarvests.status.${harvest.status}`)}
              />
            </Box>
            <Text variant="headline-small">
              {t('myHarvests.totalKg', { qty: groupDigits(harvest.totalKg) })}
            </Text>
            {harvest.crop ? (
              <Text variant="body-medium" color="text.secondary">
                {t('myHarvests.cropLabel', { crop: harvest.crop })}
              </Text>
            ) : null}
          </Stack>

          {lockMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {lockMutation.error instanceof FseApiError
                ? lockMutation.error.message
                : String(lockMutation.error)}
            </Alert>
          ) : null}

          {harvest.status !== 'open' ? (
            <Box sx={{ mt: 2 }}>
              <Text variant="body-medium" color="text.secondary">
                {t('myHarvests.lockedNote')}
              </Text>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {/* Weights section */}
      <Stack spacing={1.5}>
        <Text variant="title-large" as="h3">
          {t('myHarvests.weights.title', { count: rows.length })}
        </Text>

        {contributions.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : contributions.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => contributions.refetch()}>
                {t('myHarvests.retry')}
              </Button>
            }
          >
            {t('myHarvests.loadError', {
              error:
                contributions.error instanceof FseApiError
                  ? contributions.error.message
                  : String(contributions.error),
            })}
          </Alert>
        ) : rows.length === 0 ? (
          <Card>
            <CardContent>
              <Text variant="body-medium" color="text.secondary">
                {t('myHarvests.weights.empty')}
              </Text>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {rows.map((c, idx) => (
              <ListItem
                key={c.id}
                leadingIcon="scale"
                title={
                  <Text variant="body-medium" color="text.primary">
                    <Box component="span" sx={{ fontWeight: 600 }}>{`${groupDigits(c.qtyKg)} kg`}</Box>
                  </Text>
                }
                supportingText={t('myHarvests.weights.recordedAt', {
                  when: formatRecordedAt(c.recordedAt),
                })}
                noDivider={idx === rows.length - 1}
              />
            ))}
          </Box>
        )}
      </Stack>

      {/* Photos section — view for everyone; add only for independent farmers */}
      <PhotosSection harvestId={harvest.id} isInCoop={isInCoop} t={t} />

      {/* Actions — only while open AND not in a coop */}
      {canMutate ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button variant="contained" onClick={() => setAddOpen(true)}>
            {t('myHarvests.weights.add')}
          </Button>
          <Button variant="outlined" onClick={() => setLockOpen(true)}>
            {t('myHarvests.lock')}
          </Button>
        </Stack>
      ) : null}

      {/* Record-sale action — independent farmer on a LOCKED harvest only */}
      {showRecordSale ? (
        <Box>
          <Button variant="contained" onClick={() => setSaleOpen(true)}>
            {t('harvestSale.action')}
          </Button>
        </Box>
      ) : null}

      {/* List on marketplace — independent farmer, LOCKED harvest (parity with
          the batch detail; an on-platform alternative to recording a private sale). */}
      <PublishHarvestListingSection
        harvestId={harvest.id}
        status={harvest.status}
        isInCoop={isInCoop}
        t={t}
      />

      {/* Recorded sale — shown once sold/settled (replaces the action) */}
      {showSale ? (
        <Card>
          <CardContent>
            <Stack spacing={1.5}>
              <Text variant="title-medium" as="h3">
                {t('harvestSale.recordedTitle')}
              </Text>
              {harvest.soldTo ? (
                <SaleRow label={t('harvestSale.soldTo')} value={harvest.soldTo} />
              ) : null}
              {harvest.saleAmount != null ? (
                <SaleRow
                  label={t('harvestSale.amount')}
                  value={`${harvest.saleCurrency ?? 'ZAR'} ${groupDigits(harvest.saleAmount)}`}
                />
              ) : null}
              {harvest.saleDate ? (
                <SaleRow label={t('harvestSale.date')} value={harvest.saleDate} />
              ) : null}
              {harvest.paymentMethod ? (
                <SaleRow label={t('harvestSale.paymentMethod')} value={harvest.paymentMethod} />
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {/* Add weight dialog */}
      <Dialog open={addOpen} onClose={closeAdd} fullWidth maxWidth="sm">
        <DialogTitle>{t('myHarvests.weights.add')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t('myHarvests.weights.addBody')}</DialogContentText>
          <TextField
            label={t('myHarvests.weights.weightLabel')}
            type="number"
            value={kg}
            onChange={(e) => setKg(e.target.value)}
            error={kg.length > 0 && !kgValid}
            disabled={addMutation.isPending}
            fullWidth
          />
          {addMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('myHarvests.weights.submitError', {
                error:
                  addMutation.error instanceof FseApiError
                    ? addMutation.error.message
                    : String(addMutation.error),
              })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeAdd} disabled={addMutation.isPending}>
            {t('myHarvests.weights.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => addMutation.mutate(kgNum)}
            disabled={!kgValid || addMutation.isPending}
            startIcon={addMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('myHarvests.weights.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lock confirm dialog */}
      <Dialog open={lockOpen} onClose={closeLock} fullWidth maxWidth="sm">
        <DialogTitle>{t('myHarvests.lockConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('myHarvests.lockConfirm.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeLock} disabled={lockMutation.isPending}>
            {t('myHarvests.lockConfirm.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => lockMutation.mutate()}
            disabled={lockMutation.isPending}
            startIcon={lockMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('myHarvests.lockConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record-sale dialog (locked → sold). Currency fixed to ZAR. */}
      <Dialog open={saleOpen} onClose={closeSale} fullWidth maxWidth="sm">
        <DialogTitle>{t('harvestSale.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t('harvestSale.body')}</DialogContentText>
          <Stack spacing={2}>
            <TextField
              label={t('harvestSale.soldToLabel')}
              value={soldTo}
              onChange={(e) => setSoldTo(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 200 } }}
              disabled={saleMutation.isPending}
              fullWidth
              required
            />
            <TextField
              label={t('harvestSale.amountLabel')}
              type="number"
              value={saleAmount}
              onChange={(e) => setSaleAmount(e.target.value)}
              error={saleAmount.length > 0 && !saleAmountValid}
              disabled={saleMutation.isPending}
              fullWidth
              required
            />
            <TextField
              label={t('harvestSale.dateLabel')}
              type="date"
              value={saleDate}
              onChange={(e) => setSaleDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              disabled={saleMutation.isPending}
              fullWidth
              required
            />
            <TextField
              label={t('harvestSale.paymentMethodLabel')}
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 60 } }}
              disabled={saleMutation.isPending}
              fullWidth
            />
          </Stack>
          {saleMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('harvestSale.error', {
                error:
                  saleMutation.error instanceof FseApiError
                    ? saleMutation.error.message
                    : String(saleMutation.error),
              })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeSale} disabled={saleMutation.isPending}>
            {t('harvestSale.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              saleMutation.mutate({
                soldTo: soldTo.trim(),
                saleAmount: saleAmountNum,
                saleDate: saleDate.trim(),
                paymentMethod: paymentMethod.trim() || undefined,
              })
            }
            disabled={!saleValid || saleMutation.isPending}
            startIcon={saleMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('harvestSale.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

interface PhotosSectionProps {
  harvestId: string;
  isInCoop: boolean;
  t: TFunction;
}

/**
 * Harvest photos: a thumbnail grid + (for independent farmers) an upload
 * control. View is available to everyone who can read the harvest; the add
 * affordance follows the same independent/coop gate as record-sale (coop
 * members never see it, and the server 403s them regardless).
 */
function PhotosSection({ harvestId, isInCoop, t }: PhotosSectionProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Inline client-side validation message (non-image / too-large) shown before
  // we ever hit the network.
  const [validationError, setValidationError] = useState<string | null>(null);

  const photosQuery = useQuery({
    queryKey: ['harvest-photos', harvestId],
    queryFn: () => listHarvestPhotos(harvestId),
    enabled: harvestId.length > 0,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadHarvestPhoto(harvestId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['harvest-photos', harvestId] });
    },
    onSettled: () => {
      // Reset the input so re-selecting the same file fires `onChange` again.
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
  });

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    setValidationError(null);
    uploadMutation.reset();
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setValidationError(t('harvestPhotos.invalidType'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setValidationError(t('harvestPhotos.tooLarge'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    uploadMutation.mutate(file);
  }

  const photos = photosQuery.data ?? [];
  const canAdd = !isInCoop;

  return (
    <Stack spacing={1.5}>
      <Text variant="title-large" as="h3">
        {t('harvestPhotos.title')}
      </Text>

      {photosQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : photosQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => photosQuery.refetch()}>
              {t('myHarvests.retry')}
            </Button>
          }
        >
          {t('harvestPhotos.loadError', {
            error:
              photosQuery.error instanceof Error
                ? photosQuery.error.message
                : String(photosQuery.error),
          })}
        </Alert>
      ) : photos.length === 0 ? (
        <Card>
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('harvestPhotos.empty')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 1.5,
          }}
        >
          {photos.map((photo) => (
            <PhotoThumb key={photo.id} harvestId={harvestId} photo={photo} t={t} />
          ))}
        </Box>
      )}

      {canAdd ? (
        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onPickFile}
            style={{ display: 'none' }}
          />
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            startIcon={
              uploadMutation.isPending ? <CircularProgress size={16} /> : <Icon name="add" />
            }
          >
            {uploadMutation.isPending
              ? t('harvestPhotos.uploading')
              : t('harvestPhotos.add')}
          </Button>

          {validationError ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {validationError}
            </Alert>
          ) : null}

          {uploadMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('harvestPhotos.uploadError', {
                error:
                  uploadMutation.error instanceof Error
                    ? uploadMutation.error.message
                    : String(uploadMutation.error),
              })}
            </Alert>
          ) : null}
        </Box>
      ) : null}
    </Stack>
  );
}

interface PhotoThumbProps {
  harvestId: string;
  photo: HarvestPhotoDto;
  t: TFunction;
}

/**
 * One photo thumbnail. The image bytes sit behind auth, so we can't use the
 * URL directly in `<img src>` — we fetch it with the bearer header → blob →
 * object URL, and revoke that URL on unmount (and whenever it changes) to
 * avoid leaking blobs.
 */
function PhotoThumb({ harvestId, photo, t }: PhotoThumbProps) {
  const urlQuery = useQuery({
    queryKey: ['harvest-photo-url', photo.id],
    queryFn: () => fetchHarvestPhotoObjectUrl(harvestId, photo.id),
  });

  const objectUrl = urlQuery.data;

  useEffect(() => {
    if (!objectUrl) return;
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '1 / 1',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {urlQuery.isPending ? (
        <CircularProgress size={20} />
      ) : urlQuery.isError ? (
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Icon name="error" />
          <Text variant="label-small" color="text.secondary">
            {t('harvestPhotos.thumbError')}
          </Text>
        </Box>
      ) : objectUrl ? (
        <Box
          component="img"
          src={objectUrl}
          alt={t('harvestPhotos.thumbAlt')}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : null}
    </Box>
  );
}

/** Label/value row for the recorded-sale details panel. */
function SaleRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Text variant="body-medium" color="text.secondary">
        {label}
      </Text>
      <Text variant="body-medium" color="text.primary" align="right">
        {value}
      </Text>
    </Box>
  );
}
